/**
 * Un campo propio dicho en texto.
 *
 * Los campos propios se EDITAN con un control por tipo (`CustomFieldsSection`).
 * Pero hay tres lugares donde el valor sólo se lee —la ficha de quien no puede
 * escribir, la tarjeta del tablero y una celda de la tabla— y los tres tienen
 * que decir lo mismo que el control mostraría, sin el control.
 *
 * Lo que se guarda manda sobre lo que se definió: un valor que no está entre
 * las opciones de su lista (la opción se renombró, o lo escribió una
 * integración) se lee tal cual. Esconderlo sería borrar de la pantalla un dato
 * que el registro sí tiene.
 *
 * Módulo PURO: sin React y sin red.
 */

import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatMoney } from '@/lib/utils';
import type { CustomFieldDef, CustomFieldValue } from '@/types';

/** El valor sin saber de qué tipo es el campo. Vacío es `''`. */
export function textoDeValor(value: CustomFieldValue | undefined): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.filter((item) => item !== '').join(', ');
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
}

/** El mismo formato que muestra el selector de fecha cerrado. */
const dia = (stored: string): string => {
  const parsed = new Date(stored);
  // Lo que no es una fecha se muestra como vino: es un dato mal cargado, y
  // callarlo deja a quien mira sin saber que hay algo que corregir.
  return Number.isNaN(parsed.getTime()) ? stored : format(parsed, 'd MMM yyyy', { locale: es });
};

/**
 * El valor como lo muestra su control, según el tipo que declaró la organización.
 *
 * `''` es «sin valor», salvo en un Sí/No: ahí lo que no es «Sí» es «No», que es
 * lo que dice el interruptor apagado.
 */
export function textoDeCampo(
  field: Pick<CustomFieldDef, 'type'>,
  value: CustomFieldValue | undefined,
  currency: string,
): string {
  switch (field.type) {
    case 'boolean':
      return value === true ? 'Sí' : 'No';

    case 'date':
      return typeof value === 'string' && value !== '' ? dia(value) : '';

    case 'currency': {
      const amount = typeof value === 'number' ? value : Number(value);
      const escrito = value !== null && value !== undefined && value !== '';
      return escrito && Number.isFinite(amount) ? formatMoney(amount, currency) : textoDeValor(value);
    }

    default:
      return textoDeValor(value);
  }
}

export function listaDesdeTexto(texto: string): string[] {
  const vistos = new Map<string, string>();
  for (const parte of texto.split(',')) {
    const limpio = parte.trim();
    if (limpio !== '' && !vistos.has(limpio.toLowerCase())) vistos.set(limpio.toLowerCase(), limpio);
  }
  return [...vistos.values()];
}
